import { SubmitHandler, useForm } from "react-hook-form";
import {
  Box,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  IconButton,
  Input,
  Radio,
  RadioGroup,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  Wrap,
} from "@chakra-ui/react";
import { DeleteIcon, SmallCloseIcon } from "@chakra-ui/icons";
import { TalesSpinner } from "../../../../atoms";
import { useDispatch, useSelector } from "react-redux";
import { AppThunkDispatch } from "../../../../../store";
import {
  getAreeData,
  getAreeError,
  isAreeLoading,
} from "../../../../../store/aree/selectors";
import { addArea } from "../../../../../store/aree/actions/add";
import { editArea } from "../../../../../store/aree/actions/edit";
import { toggleAreaVisibility } from "../../../../../store/aree/actions/toggleVisibility";
import { deleteArea } from "../../../../../store/aree/actions/delete";
import NewGuestInput from "./atoms/NewGuestInput";
import useAree from "../../../../../utils/hooks/useAree";

interface AreaInput {
  title: string;
  guest: string;
  show: boolean;
  order: number;
}

export default function Aree() {
  useAree();
  const dispatch = useDispatch<AppThunkDispatch>();
  const isPending = useSelector(isAreeLoading);
  const aree = useSelector(getAreeData);
  const error = useSelector(getAreeError);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    getValues,
  } = useForm<AreaInput>({
    defaultValues: {
      title: "",
      guest: "",
      order: (aree?.length ?? 0) + 1,
      show: true,
    },
  });

  const onSubmit: SubmitHandler<AreaInput> = (data) => {
    const newArea = {
      title: getValues("title"),
      guests: [getValues("guest")],
      show: getValues("show"),
      order: getValues("order"),
    };
    dispatch(addArea(newArea));
  };

  return (
    <>
      {error && (
        <Box>
          <Text my="10px">{error}</Text>
        </Box>
      )}
      <Wrap>
        {aree &&
          aree.map((area) => (
            <Card key={area.title} w="xs" m="10px">
              <CardHeader>
                <Heading size="base">{area.title}</Heading>
                {area.order && <Text>Ordine: {area.order}</Text>}
              </CardHeader>
              <CardBody>
                {area.guests.map((guest) => (
                  <Box key={guest} my={"10px"}>
                    <Tag colorScheme="gray" size="lg">
                      <TagLabel>{guest}</TagLabel>
                      <TagCloseButton
                        onClick={() =>
                          dispatch(
                            editArea({
                              ...area,
                              guests: area.guests.filter((g) => g !== guest),
                            }),
                          )
                        }
                      />
                    </Tag>
                  </Box>
                ))}
                <NewGuestInput area={area} />
              </CardBody>
              <CardFooter>
                <Button
                  aria-label={area.show ? "Nascondi" : "Mostra"}
                  variant="solid"
                  colorScheme={area.show ? "gray" : "green"}
                  onClick={() => dispatch(toggleAreaVisibility(area))}
                  marginRight="20px"
                >
                  {area.show ? "NASCONDI DAL SITO" : "MOSTRA SUL SITO"}
                </Button>
                <IconButton
                  aria-label="Elimina"
                  variant="solid"
                  icon={<DeleteIcon />}
                  colorScheme="red"
                  onClick={() => dispatch(deleteArea(area))}
                />
              </CardFooter>
            </Card>
          ))}
        <Card w="xs" m="10px">
          {isPending && (
            <CardBody>
              <Box w="30px" m="auto">
                <TalesSpinner />
              </Box>
            </CardBody>
          )}
          {!isPending && (
            <form onSubmit={handleSubmit(onSubmit)}>
              <CardHeader>
                <Heading size="base">Aggiungi nuovo</Heading>
              </CardHeader>
              <CardBody>
                <FormControl isRequired={true} marginBottom="20px">
                  <FormLabel htmlFor="order">Ordine</FormLabel>
                  <Input id="order" type="number" {...register("order")} />
                  <FormErrorMessage>
                    {errors.title && errors.title.message}
                  </FormErrorMessage>
                </FormControl>
                <FormControl marginBottom="20px" isRequired={true}>
                  <FormLabel htmlFor="title">Area</FormLabel>
                  <Input
                    id="title"
                    type="text"
                    {...register("title", { required: "Campo richiesto" })}
                  />
                  <FormErrorMessage>
                    {errors.title && errors.title.message}
                  </FormErrorMessage>
                </FormControl>
                <FormControl isRequired={true} marginBottom="20px">
                  <FormLabel htmlFor="guest">Partecipante</FormLabel>
                  <Input
                    type="text"
                    id="guest"
                    {...register("guest", { required: "Campo richiesto" })}
                  />
                  <FormErrorMessage>
                    {errors.guest && errors.guest.message}
                  </FormErrorMessage>
                </FormControl>
                <FormControl>
                  <FormLabel>Mostrare sul sito</FormLabel>
                  <RadioGroup defaultValue={getValues("show").toString()}>
                    <Flex>
                      <Radio
                        value="true"
                        marginRight="30px"
                        {...register("show")}
                      >
                        Sì
                      </Radio>
                      <Radio value="false" {...register("show")}>
                        No
                      </Radio>
                    </Flex>
                  </RadioGroup>
                </FormControl>
              </CardBody>
              <CardFooter>
                <Button
                  type="submit"
                  isDisabled={
                    isPending ||
                    watch("title") === "" ||
                    watch("guest") === "" ||
                    aree?.some((area) => area.title === watch("title"))
                  }
                  colorScheme="green"
                >
                  Crea
                </Button>
              </CardFooter>
            </form>
          )}
        </Card>
      </Wrap>
    </>
  );
}
